if (!customElements.get('device-compatibility-checker')) {
  customElements.define(
    'device-compatibility-checker',
    class DeviceCompatibilityChecker extends HTMLElement {
      constructor() {
        super();

        this.popup = this.querySelector('.device__comp-popup');
        this.popupPanel = this.querySelector('.device__comp-inner');
        this.swipeHandle = this.querySelector('.device__comp-header');
        this.closeBtn = this.querySelector('.device__comp-close');
        this.onPopupClick = this.popupClick.bind(this);
        this.onSearchInput = null;
        this.searchInput = null;
        this.controller = null;
        this.isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.isPDPUplift = this.popup?.classList.contains('cv__device-compatibility') ?? false;
        // this.usePDPUpliftBehavior = this.isPDPUplift && this.isMobile;
        this.value = '';

        this.mobileTrigger = this.querySelector('.device__comp-btn--container');
        this.checkDeviceCompatibilityButton = this.querySelector('.device__comp-button');
        this.onMobileTriggerKeydown = this.handleMobileTriggerKeydown.bind(this);
      }
      connectedCallback() {
        const init = () => {
          if (!this.isMobile && this.isPDPUplift) {
            this.popup?.classList.remove('cv__device-compatibility');
          }

          this.mobileTrigger?.setAttribute('role', 'button');
          this.mobileTrigger?.setAttribute('tabindex', '0');
          this.mobileTrigger?.setAttribute('aria-haspopup', 'dialog');
          this.mobileTrigger?.addEventListener('keydown', this.onMobileTriggerKeydown);

          this.controller = window.createBacksheetController({
            host: this,
            overlay: this.popup,
            panel: this.popupPanel,
            closeBtn: this.closeBtn,
            swipeHandle: this.swipeHandle,
            openTriggers: this.mobileTrigger ? [this.mobileTrigger] : [],
            visibleClass: 'device__comp-popup--visible',
            moveBackDelay: 350,
            lock: this.lockBody.bind(this),
            unlock: this.unlockBody.bind(this),
            shouldEnableSwipe: () => this.popup?.classList.contains('cv__device-compatibility'),
            onOpen: () => {
              if (this.isMobile) {
                this.popup?.addEventListener('click', this.onPopupClick);
              }
            },
            onClose: () => {
              this.popup?.removeEventListener('click', this.onPopupClick);
            },
          });
          this.controller.bind();
          this.handleSearch();
          this.fetchCompatibleDevices();
        };

        // Initialise once DOM is fully loaded
        document.readyState === 'loading'
          ? document.addEventListener('DOMContentLoaded', init, { once: true })
          : init();
      }

      handleMobileTriggerKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        this.controller?.open();
      }

      disconnectedCallback() {
        this.controller?.unbind();
        if (this.searchInput && this.onSearchInput) {
          this.searchInput.removeEventListener('input', this.onSearchInput);
        }
        this.mobileTrigger?.removeEventListener('keydown', this.onMobileTriggerKeydown);
        clearTimeout(this.searchDebounceTimer);
        this.unlockBody();
      }

      openDeviceCompatibilityPopup() {
        this.controller?.open();
      }

      closeDeviceCompatibilityPopup() {
        this.checkDeviceCompatibilityButton?.blur();
        this.controller?.close();
      }

      lockBody() {
        const isCvPopup = this.popup?.classList.contains('cv__device-compatibility');
        document.documentElement.classList.add('dc-lock');
        document.body.classList.add('dc-lock');
        document.documentElement.classList.toggle('dc-lock--cv', isCvPopup);
        document.body.classList.toggle('dc-lock--cv', isCvPopup);

        if (window.scrollLock?.lock) {
          window.scrollLock.lock();
          return;
        }

        document.body.style.overflow = 'hidden';
      }

      unlockBody() {
        document.documentElement.classList.remove('dc-lock');
        document.body.classList.remove('dc-lock');
        document.documentElement.classList.remove('dc-lock--cv');
        document.body.classList.remove('dc-lock--cv');

        if (window.scrollLock?.unlock) {
          window.scrollLock.unlock();
          return;
        }

        document.body.style.overflow = '';
      }

      popupClick(event) {
        if (event.target === this.popup) {
          this.closeDeviceCompatibilityPopup();
        }
      }

      openSupportChat() {
        // Prefer vendor API if available.
        if (window.GorgiasChat?.open) {
          window.GorgiasChat.open();
          return;
        }

        // Fallback to known launcher elements.
        const launcher = document.querySelector(
          '#gorgias-chat-container, button#chat-button, button[class*="gorgias"], [aria-label*="chat" i]',
        );
        if (launcher) {
          launcher.click();
        }
      }

      async handleSearch() {
        this.popularDevicesContainer = this.querySelector('.device__comp-popular');
        this.loader = this.popularDevicesContainer?.querySelector('.device__loader');
        this.noResultsContainer = this.querySelector('.device__comp-no--results');
        this.searchResultsContainer = this.querySelector('.device__comp-results');
        this.disclaimer = this.querySelector('.device__comp-disclaimer');
        this.supportLink = this.querySelector('.contact-us');
        this.setInitialSearchState();

        this.searchInput = this.querySelector('.device-compatibility-search');
        if (!this.searchInput) return;

        if (this.onSearchInput) {
          this.searchInput.removeEventListener('input', this.onSearchInput);
        }

        this.onSearchInput = (e) => {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = setTimeout(() => {
            this.value = e.target.value;
            if (this.value.length === 0) {
              this.noResultsContainer.classList.add('results__hidden');
              // if (this.usePDPUpliftBehavior) {
              this.showAllCompatibleDevices();
              // } else {
              //   this.clearSearchResults();
              // }
              // this.updateDisclaimerVisibility('');
            } else {
              this.scrollToTop();
              this.matchCompatibleDevices(this.value.toLowerCase());
              this.sendGtagEvent();
              // this.updateDisclaimerVisibility(this.value);
            }
          }, 150);
        };

        this.searchInput.addEventListener('input', this.onSearchInput);
        this.supportLink?.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeDeviceCompatibilityPopup();
          // Wait for popup close transition so chat launcher can receive interaction.
          setTimeout(() => this.openSupportChat(), 350);
        });
      }

      scrollToTop() {
        const scrollableElement = this.popup.querySelector('.device__comp-results--list');
        if (!scrollableElement) return;

        scrollableElement.scrollTo({
          top: 0,
          behavior: 'smooth', // Optional: use 'auto' for an instant jump
        });
      }

      sendGtagEvent() {
        try {
          gtag('event', 'device_search', {
            search_term: this.value,
            page_title: document.title,
            page: window.location.href,
          });
        } catch (e) {
          console.error('There has been an error', e);
        }
      }

      async fetchCompatibleDevices() {
        if (this.allDevices) return;

        this.storefrontToken =
          this.getAttribute('data-token') || window?.themeSettings?.storefrontToken;

        if (!this.storefrontToken) {
          return;
        }

        this.allDevices = [];
        let hasNextPage = true;
        let cursor = null;

        this.setLoadingState(true);

        try {
          while (hasNextPage) {
            const query = `query FetchCompatibleDevices($first: Int!, $after: String) {
          metaobjects(type: "compatible_device", first: $first, after: $after) {
            edges {
              node {
                fields { key value }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }`;

            const variables = { first: 50, after: cursor };

            const res = await fetch('/api/2024-07/graphql.json', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.storefrontToken,
              },
              body: JSON.stringify({ query, variables }),
            });

            if (!res.ok) {
              throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }

            const json = await res.json();

            if (json.errors) {
              throw new Error('Failed to fetch countries');
            }

            const devices = json.data?.metaobjects?.edges || [];
            const nodes = devices?.map((device) => device.node.fields);

            if (!nodes) return;

            this.allDevices.push(...nodes);

            hasNextPage = json.data?.metaobjects?.pageInfo?.hasNextPage || false;
            cursor = json.data?.metaobjects?.pageInfo?.endCursor;
          }
        } catch (error) {
          throw error;
        } finally {
          this.setLoadingState(false);
        }

        // if (this.allDevices && !this.usePDPUpliftBehavior) {
        //   this.showPopularDevices();
        // } else if (this.allDevices && this.usePDPUpliftBehavior) {
        this.showAllCompatibleDevices();
        // }
      }

      showPopularDevices() {
        this.popularDevices = this.allDevices.filter((device) => {
          const popular = device.find((item) => item.key === 'popular')?.value;
          return popular === 'true';
        });

        this.popularDevicesContainerList =
          this.popularDevicesContainer?.querySelector('ul') ||
          this.popup?.querySelector('.device__comp-popular ul');
        const popularDisclaimer = this.popularDevicesContainerList.querySelector(
          '.device__comp-disclaimer',
        );
        if (!this.popularDevicesContainerList) return;
        this.popularDevicesContainerList.innerHTML = '';

        const fragment = document.createDocumentFragment();

        this.popularDevices.forEach((device) => {
          const li = document.createElement('li');
          li.textContent = device.find((item) => item.key === 'name')?.value;
          const span = document.createElement('span');
          span.innerHTML = `Compatible <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
<path d="M11 0C4.935 0 0 4.935 0 11C0 17.065 4.935 22 11 22C17.065 22 22 17.065 22 11C22 4.935 17.065 0 11 0ZM9.75286 15.6585C9.37195 16.0939 8.70221 16.1162 8.29317 15.7072L5.293 12.707C4.90253 12.3165 4.90253 11.6835 5.293 11.293C5.68347 10.9025 6.31653 10.9025 6.707 11.293L8.951 13.537L15.2475 6.34154C15.6112 5.92592 16.2429 5.88383 16.6585 6.24753C17.0741 6.6112 17.1162 7.2429 16.7526 7.65851L9.75286 15.6585Z" fill="#089F55"/>
</svg>`;
          li.appendChild(span);

          fragment.appendChild(li);
        });

        if (popularDisclaimer) {
          fragment.appendChild(popularDisclaimer);
        }

        this.popularDevicesContainerList.appendChild(fragment);
      }

      setLoadingState(isLoading) {
        if (!this.searchResultsContainer) return;

        this.searchResultsContainer.classList.toggle('is-loading', isLoading);

        if (this.loader) {
          this.loader.setAttribute('aria-hidden', String(!isLoading));
        }
      }

      matchCompatibleDevices(str) {
        const normalizedQuery = (str || '').trim().toLowerCase();

        if (
          !normalizedQuery
          // && this.usePDPUpliftBehavior
        ) {
          this.showAllCompatibleDevices();
          return;
        }

        // Split the search query by spaces into an array of tokens and filter out any falsey values (like empty strings)
        const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);

        const matchingDevices = (this.allDevices || [])
          .map((device) => {
            const name = device.find((item) => item.key === 'name')?.value?.toLowerCase();
            if (!name) return null;
            // Exact substring matches are considered strongest matches.
            if (name.includes(normalizedQuery)) return { device, score: 0 };

            const nameTokens = name.split(/\s+/).filter(Boolean);
            const tokenDistances = queryTokens.map((queryToken) =>
              Math.min(
                ...nameTokens.map((nameToken) => levenshteinDistance(queryToken, nameToken)),
              ),
            );

            if (tokenDistances.some((distance) => distance >= 2)) return null;

            return {
              device,
              score: tokenDistances.reduce((sum, distance) => sum + distance, 0),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.score - b.score)
          .map(({ device }) => device);

        this.showSearchResults(matchingDevices);
      }

      showAllCompatibleDevices() {
        this.showSearchResults(this.allDevices || []);
      }

      updateSearchResultsHeading() {
        if (!this.searchResultsContainer) return;

        const heading = this.searchResultsContainer.querySelector('p');
        const searchTermSpan = this.searchResultsContainer.querySelector(
          '.device__comp-search--term',
        );

        if (!heading || !searchTermSpan) return;

        heading.innerHTML =
          'Compatible Devices matching "<span class="device__comp-search--term"></span>"';
        this.searchResultsContainer.querySelector('.device__comp-search--term').textContent =
          this.value;

        this.value ? heading.classList.remove('hidden') : heading.classList.add('hidden');
      }

      showSearchResults(arr) {
        if (!arr || arr.length === 0) {
          const span = this.noResultsContainer.querySelector('.device__comp-search--term');
          if (!span) return;
          span.textContent = this.value;
          this.noResultsContainer.classList.remove('results__hidden');
          // if (!this.usePDPUpliftBehavior) {
          //   this.popularDevicesContainer.classList.add('results__hidden');
          // }
          this.searchResultsContainer.classList.add('results__hidden');
          // this.updateDisclaimerVisibility(this.value);
          return;
        }
        this.updateSearchResultsHeading();

        this.noResultsContainer.classList.add('results__hidden');

        // if (this.usePDPUpliftBehavior) {
        this.searchResultsContainer.classList.remove('results__hidden');
        // } else {
        //   this.toggleVisibility(this.searchResultsContainer, this.popularDevicesContainer, true);
        // }

        const fragment = document.createDocumentFragment();

        arr.forEach((device) => {
          const li = document.createElement('li');
          li.textContent = device[0]?.value;
          const span = document.createElement('span');
          span.innerHTML = `Compatible <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
<path d="M11 0C4.935 0 0 4.935 0 11C0 17.065 4.935 22 11 22C17.065 22 22 17.065 22 11C22 4.935 17.065 0 11 0ZM9.75286 15.6585C9.37195 16.0939 8.70221 16.1162 8.29317 15.7072L5.293 12.707C4.90253 12.3165 4.90253 11.6835 5.293 11.293C5.68347 10.9025 6.31653 10.9025 6.707 11.293L8.951 13.537L15.2475 6.34154C15.6112 5.92592 16.2429 5.88383 16.6585 6.24753C17.0741 6.6112 17.1162 7.2429 16.7526 7.65851L9.75286 15.6585Z" fill="#089F55"/>
</svg>`;
          li.appendChild(span);
          fragment.appendChild(li);
        });

        this.clearSearchResults(fragment);
      }

      clearSearchResults(fragment) {
        const searchResultsList = this.searchResultsContainer.querySelector('ul');
        searchResultsList.innerHTML = '';

        if (fragment) {
          searchResultsList.appendChild(fragment);
          searchResultsList.appendChild(this.disclaimer);
        } else {
          // if (this.usePDPUpliftBehavior) {
          this.searchResultsContainer.classList.remove('results__hidden');
          // } else {
          //   this.toggleVisibility(this.searchResultsContainer, this.popularDevicesContainer, false);
          // }
        }
      }

      toggleVisibility(searchResultsContainer, popularDevicesContainer, showResults) {
        if (showResults) {
          searchResultsContainer.classList.remove('results__hidden');
          popularDevicesContainer.classList.add('results__hidden');
        } else {
          searchResultsContainer.classList.add('results__hidden');
          popularDevicesContainer.classList.remove('results__hidden');
          popularDevicesContainer.querySelector('ul')?.appendChild(this.disclaimer);
        }
        // this.updateDisclaimerVisibility(this.value);
      }

      setInitialSearchState() {
        if (
          !this.popularDevicesContainer ||
          !this.noResultsContainer ||
          !this.searchResultsContainer
        ) {
          return;
        }

        this.noResultsContainer.classList.add('results__hidden');
        // this.searchResultsContainer.classList.toggle('results__hidden', !this.usePDPUpliftBehavior);

        // if (!this.usePDPUpliftBehavior) {
        //   this.popularDevicesContainer.classList.remove('results__hidden');
        // }

        // this.updateDisclaimerVisibility('');
      }

      updateDisclaimerVisibility(searchValue) {
        if (
          // this.usePDPUpliftBehavior ||
          !this.disclaimer ||
          !this.popularDevicesContainer ||
          !this.searchResultsContainer
        ) {
          return;
        }
        const normalizedSearch = (searchValue || '').trim().toLowerCase();
        const hasIphone =
          normalizedSearch !== '' &&
          ('iphone'.startsWith(normalizedSearch) || normalizedSearch.startsWith('iphone'));
        const showingPopular = !this.popularDevicesContainer.classList.contains('results__hidden');
        const showingResults = !this.searchResultsContainer.classList.contains('results__hidden');
        const hasResults = this.searchResultsContainer.querySelectorAll('li').length > 0;
        const shouldShow = showingPopular || (showingResults && hasResults && hasIphone);
        this.disclaimer.classList.toggle('is-hidden', !shouldShow);
      }
    },
  );
}
